// The DPIA screening on a processing activity and the criteria record on a DPIA: GDPR Art. 35(3)
// and the nine WP248 criteria (lib/dpiaCriteria.ts, mirror of the backend rule). Criteria the record
// already shows are ticked and locked with the reason; the rest are the member's answers.

import { theme, type as text, inputStyle } from "../theme";
import {
  ART35_3_CASES, NOT_REQUIRED_REASON_MIN, WP248_CRITERIA, dpiaScreening, type DpiaCriteriaRecord, type DpiaLevel, type DpiaScreening,
} from "../lib/dpiaCriteria";

export const DPIA_LEVEL_LABELS: Record<DpiaLevel, string> = {
  mandatory: "DPIA mandatory",
  likely: "DPIA likely to be required",
  consider: "One criterion met: decide",
  none: "No criteria met",
};

const LEVEL_TONE: Record<DpiaLevel, { bg: string; fg: string }> = {
  mandatory: { bg: "var(--c-dangerBg)", fg: "var(--c-dangerText)" },
  likely:    { bg: "var(--c-warningBg)", fg: "var(--c-warningText)" },
  consider:  { bg: "var(--c-accentBg)", fg: "var(--c-accentText)" },
  none:      { bg: "var(--c-faint)", fg: "var(--c-muted)" },
};

export function DpiaLevelTag({ level }: { level: DpiaLevel }) {
  const t = LEVEL_TONE[level];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 2, background: t.bg, color: t.fg }}>{DPIA_LEVEL_LABELS[level]}</span>;
}

const toggle = (xs: readonly string[], id: string) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);
const rowStyle = { ...text.hint, color: theme.color.text, display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" } as const;

export interface RopaScreeningValue {
  dpiaCriteria: string[];
  dpiaMandatoryCases: string[];
  dpiaScreened: boolean;
  dpiaNotRequiredReason: string;
  /** Declared: the activity monitors workers (criteria 3 and 7 follow). */
  monitorsWorkers: boolean;
}

/** The screening section of the RoPA form, worked out live from the form's own categories and scale. */
export function RopaDpiaScreeningFields({ record, aiRiskClasses, ukLaw, value, onChange }: {
  /** `declaredUkTypes`: the ICO Art. 35(4) list items the member declared (read only while `ukLaw`). */
  record: { dataCategories: string[]; subjectCategories: string[]; largeScale: boolean; declaredUkTypes?: string[] };
  aiRiskClasses: string[];
  /** A UK data protection law is in scope: the ICO's Art. 35(4) list applies and any linked AI system counts toward criterion 8. */
  ukLaw?: boolean;
  value: RopaScreeningValue;
  onChange: (next: RopaScreeningValue) => void;
}) {
  const s = dpiaScreening({ ...record, aiRiskClasses, ukLaw, monitorsWorkers: value.monitorsWorkers, declaredCriteria: value.dpiaCriteria, declaredCases: value.dpiaMandatoryCases });
  const set = (patch: Partial<RopaScreeningValue>) => onChange({ ...value, ...patch });
  const reasonShort = value.dpiaNotRequiredReason.trim().length > 0 && value.dpiaNotRequiredReason.trim().length < NOT_REQUIRED_REASON_MIN;
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <DpiaLevelTag level={s.level} />
        <span style={text.hint}>
          {s.level === "mandatory" ? (s.cases.some((c) => c.id.startsWith("ico_")) && !s.cases.some((c) => !c.id.startsWith("ico_")) ? "It is on the ICO's Art. 35(4) list." : "An Art. 35(3) case applies.") : `${s.metCount} of 9 criteria met. Two or more usually mean a DPIA; one can be enough.`}
        </span>
      </div>

      <label style={rowStyle}>
        <input type="checkbox" checked={value.monitorsWorkers} style={{ marginTop: 2 }} onChange={(e) => set({ monitorsWorkers: e.target.checked })} />
        <span>
          <strong style={{ fontWeight: 600 }}>This activity monitors workers</strong>
          <span style={{ color: theme.color.subtle }}> — the activity, communications, location or devices of staff or contractors. Counts as systematic monitoring of people in an imbalanced relationship, so a DPIA is likely to be required (ICO monitoring workers guidance).</span>
        </span>
      </label>

      <div style={{ display: "grid", gap: 6 }}>
        {WP248_CRITERIA.map((c) => {
          const r = s.criteria.find((x) => x.id === c.id)!;
          return (
            <label key={c.id} style={{ ...rowStyle, cursor: r.derived ? "default" : "pointer" }} title={c.help}>
              <input type="checkbox" checked={r.met} disabled={r.derived} style={{ marginTop: 2 }}
                onChange={() => set({ dpiaCriteria: toggle(value.dpiaCriteria, c.id) })} />
              <span>
                <strong style={{ fontWeight: 600 }}>{c.n}. {c.label}</strong>
                {r.derived ? <span style={{ color: theme.color.subtle }}> — from this record: {r.reason}</span> : <span style={{ color: theme.color.subtle }}> — {c.help}</span>}
              </span>
            </label>
          );
        })}
      </div>

      <div>
        <div style={{ ...text.label, marginBottom: 4 }}>Mandatory cases (GDPR Art. 35(3))</div>
        <div style={{ display: "grid", gap: 6 }}>
          {ART35_3_CASES.map((c) => {
            const derived = s.cases.find((x) => x.id === c.id)?.derived ?? false;
            const on = derived || value.dpiaMandatoryCases.includes(c.id);
            return (
              <label key={c.id} style={{ ...rowStyle, cursor: derived ? "default" : "pointer" }} title={c.help}>
                <input type="checkbox" checked={on} disabled={derived} style={{ marginTop: 2 }}
                  onChange={() => set({ dpiaMandatoryCases: toggle(value.dpiaMandatoryCases, c.id) })} />
                <span>{c.label}{derived ? <span style={{ color: theme.color.subtle }}> — from this record</span> : null}</span>
              </label>
            );
          })}
        </div>
      </div>

      <label style={rowStyle}>
        <input type="checkbox" checked={value.dpiaScreened} onChange={(e) => set({ dpiaScreened: e.target.checked })} style={{ marginTop: 2 }} />
        <span>I have screened this activity against all nine criteria</span>
      </label>

      {(s.level === "likely" || s.level === "consider") && (
        <div>
          <div style={{ ...text.label, marginBottom: 4 }}>If no DPIA is needed, why not</div>
          <textarea value={value.dpiaNotRequiredReason} onChange={(e) => set({ dpiaNotRequiredReason: e.target.value })} rows={2}
            placeholder="Why this processing is not likely to result in a high risk to people. Leave empty if a DPIA will be done."
            style={{ ...inputStyle, fontSize: 13, resize: "vertical", width: "100%" }} />
          {reasonShort && <div style={{ ...text.hint, color: "var(--c-warningText)" }}>At least {NOT_REQUIRED_REASON_MIN} characters.</div>}
        </div>
      )}
    </div>
  );
}

/** The screening as the record view shows it. */
export function DpiaScreeningSummary({ screening }: { screening: Pick<DpiaScreening, "level" | "criteria" | "cases"> & { notRequired?: boolean; screened?: boolean } }) {
  const met = screening.criteria.filter((c) => c.met);
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <DpiaLevelTag level={screening.level} />
        {screening.notRequired && <span style={text.hint}>Recorded as not needing a DPIA, with reasons.</span>}
        {!screening.screened && <span style={text.hint}>Screening not yet confirmed.</span>}
      </div>
      {screening.cases.map((c) => <div key={c.id} style={{ ...text.hint, color: theme.color.text }}>{c.label}</div>)}
      {met.length > 0
        ? met.map((c) => <div key={c.id} style={{ ...text.hint, color: theme.color.text }}>{c.n}. {c.label}{c.reason ? ` (${c.reason})` : ""}</div>)
        : <div style={text.hint}>None of the nine criteria met.</div>}
    </div>
  );
}

/** A DPIA's own record against the nine criteria: applies / does not apply, and why. */
export function DpiaCriteriaFields({ value, cases, onChange, onCasesChange }: {
  value: DpiaCriteriaRecord;
  cases: string[];
  onChange: (next: DpiaCriteriaRecord) => void;
  onCasesChange: (next: string[]) => void;
}) {
  const setOne = (id: string, patch: { met?: boolean | null; note?: string }) => {
    const cur = value[id];
    const next = { ...value };
    if (patch.met === null) { delete next[id]; onChange(next); return; }
    next[id] = { met: patch.met ?? cur?.met ?? false, ...(patch.note !== undefined ? (patch.note ? { note: patch.note } : {}) : cur?.note ? { note: cur.note } : {}) };
    onChange(next);
  };
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {WP248_CRITERIA.map((c) => {
        const cur = value[c.id];
        return (
          <div key={c.id} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 140px", gap: 8, alignItems: "start" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>{c.n}. {c.label}</div>
              <div style={text.hint}>{c.help}</div>
              {cur && (
                <input value={cur.note ?? ""} onChange={(e) => setOne(c.id, { note: e.target.value })} maxLength={2000}
                  placeholder={cur.met ? "How it applies" : "Why it does not apply"} aria-label={`Note on criterion ${c.n}`}
                  style={{ ...inputStyle, fontSize: 12, marginTop: 4, width: "100%" }} />
              )}
            </div>
            <select value={cur ? (cur.met ? "yes" : "no") : ""} aria-label={`Criterion ${c.n} applies`}
              onChange={(e) => setOne(c.id, { met: e.target.value === "" ? null : e.target.value === "yes" })}
              style={{ ...inputStyle, fontSize: 13 }}>
              <option value="">Not answered</option>
              <option value="yes">Applies</option>
              <option value="no">Does not apply</option>
            </select>
          </div>
        );
      })}
      <div>
        <div style={{ ...text.label, marginBottom: 4 }}>Mandatory cases (GDPR Art. 35(3))</div>
        {ART35_3_CASES.map((c) => (
          <label key={c.id} style={{ ...rowStyle, marginBottom: 4 }} title={c.help}>
            <input type="checkbox" checked={cases.includes(c.id)} onChange={() => onCasesChange(toggle(cases, c.id))} style={{ marginTop: 2 }} />
            <span>{c.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
